import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Col, Container, Row } from 'reactstrap';
import debounce from '../../../helpers/debounce';
import {
  createErrorMessage,
  createSuccessMessage,
  createWarningMessage,
} from '../../../helpers/notification';
import Confirm from '../../../shared/components/Confirm';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import { dateDDMMYYFormat } from '../../../helpers/dateFormat';
import { fetchExam, fetchExams } from '../../../redux/actions/examActions';
import { moveExamInscriptionsByExam } from '../../../redux/actions/studentSubjectsActions';
import MoveExamInscriptionsForm from './MoveExamInscriptionsForm';

class MoveExamInscriptionsIndex extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      sourceExam: null,
      students: [],
      exams: [],
      exam: { value: 0 },
      filterValue: '',
      checkAll: false,
    };

    this.debouncedHandleFilterChangeText = debounce(this.handleFilterChangeText, 500);
  }

  componentDidMount() {
    this.fetchFormDatas();
  }

  fetchFormDatas = () => {
    const { id } = this.props;

    fetchExam(id, {
      onSuccess: (exam) => {
        const sourceExam = {
          id: exam.id,
          periodo: exam.periodo,
          materia: exam.materia,
          carreraId: exam.carreraId,
          semestreId: exam.semestreId,
          periodoLectivoId: exam.periodoLectivoId,
          materiaPeriodoId: exam.materiaPeriodoId,
          fecha: exam.fecha,
          estado: exam.estado,
          inscriptos: exam.inscriptos || [],
        };

        const students = (exam.inscriptos || []).map(i => ({
          id: i.id,
          usuarioId: i.usuarioId,
          numeroDocumento: i.numeroDocumento,
          alumno: `${i.apellido}, ${i.nombre}`,
          anioInscripcion: i.anioInscripcion,
          selected: false,
        }));

        fetchExams({
          pageInfo: {
            page: 1,
            pageSize: 500,
            filters: {
              sourceExamId: exam.id,
            },
          },
          onSuccess: ({ items = [] }) => {
            this.setState({
              sourceExam,
              students,
              exams: this.buildExams(sourceExam, items),
              loading: false,
            });
          },
          onError: () => {
            this.setState({
              sourceExam,
              students,
              exams: [{ value: 0, label: 'Seleccione examen destino' }],
              loading: false,
            });
          },
        });
      },
      onError: () => {
        this.setState({ loading: false });
        createErrorMessage('Error al intentar cargar los datos del examen.');
      },
    });
  }

  handleSubmit = () => {
    const { sourceExam, exam } = this.state;
    const { students = [] } = this.state;

    if (!sourceExam) {
      createWarningMessage('No se pudo cargar el examen origen.');
      return;
    }

    if (!exam || !exam.value) {
      createWarningMessage('Debe seleccionar el examen destino.');
      return;
    }

    const selectedIds = students
      .filter(s => s.selected)
      .map(s => s.id);

    const selectedStudents = students
      .filter(s => s.selected)
      .map(s => `${s.numeroDocumento} - ${s.alumno}`);

    if (selectedIds.length === 0) {
      createWarningMessage('Debe seleccionar al menos un alumno.');
      return;
    }

    Confirm({
      title: 'Mover inscripciones de examen',
      question: (
        <>
          <div>
            ¿Mover {selectedIds.length} inscripciones del examen origen al examen destino #{exam.value}?
          </div>
          <div style={{ marginTop: 10, marginBottom: 5 }}>Alumnos a mover:</div>
          <ul>
            {selectedStudents.map(student => (
              <li key={student}>{student}</li>
            ))}
          </ul>
        </>
      ),
      yesLabel: 'Si, mover alumnos',
      noLabel: 'No, volver',
      onConfirm: () => {
        this.setState({ loading: true });

        moveExamInscriptionsByExam({
          data: {
            oldExamId: sourceExam.id,
            newExamId: exam.value,
            examInscriptionIds: selectedIds,
          },
          onSuccess: () => {
            this.setState({ loading: false });
            createSuccessMessage('Los alumnos fueron movidos de examen exitosamente.');
            this.handleOnBack();
          },
          onError: (error = {}) => {
            this.setState({ loading: false });
            if (error.message) {
              createWarningMessage(error.message);
            } else {
              createErrorMessage('Ocurrió un error al intentar mover alumnos de examen.');
            }
          },
        });
      },
    });
  }

  handleOnBack = () => {
    const { history } = this.props;
    history.goBack();
  }

  handleChangeSelect = (exam) => {
    this.setState({ exam });
  }

  onCheckAllChanges = (evt) => {
    if (!evt.currentTarget) {
      return false;
    }

    const checkAll = evt.target.checked;
    const { students } = this.state;

    const newStudents = students.map(s => ({
      ...s,
      selected: checkAll,
    }));

    this.setState({
      students: newStudents,
      checkAll,
    });

    return true;
  }

  onStudentEnrollmentChanges = (evt, student) => {
    if (!evt.currentTarget) {
      return false;
    }

    const { students } = this.state;
    const newStudents = [...students];

    const elementsIndex = newStudents.findIndex(s => s.id === student.id);
    newStudents[elementsIndex] = {
      ...newStudents[elementsIndex],
      selected: !newStudents[elementsIndex].selected,
    };

    this.setState({
      students: newStudents,
    });

    return true;
  }

  handleFilterChangeText = ({ target: { value } }) => {
    this.setState({ filterValue: value });
  }

  transformText = (value = '') => `${value}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  getStudents = () => {
    const {
      filterValue,
      students,
    } = this.state;

    const filter = this.transformText(filterValue);

    return students.filter(s => (
      this.transformText(`${s.numeroDocumento}`).includes(filter)
      || this.transformText(s.alumno).includes(filter)
    ));
  }

  buildExams = (sourceExam, exams = []) => {
    const byExam = exams
      .filter(item => item.id !== sourceExam.id)
      .reduce((acc, item) => {
        if (!acc[item.id]) {
          acc[item.id] = {
            value: item.id,
            label: `${item.materia} | ${item.periodo || ''} | ${dateDDMMYYFormat(item.fecha) || ''}`,
            item,
          };
        }

        return acc;
      }, {});

    return [
      { value: 0, label: 'Seleccione examen destino' },
      ...Object.values(byExam),
    ];
  }

  render() {
    const {
      sourceExam,
      loading,
      exams,
      exam,
      filterValue,
      checkAll,
    } = this.state;

    if (loading || !sourceExam) {
      return <LoadingSpinner />;
    }

    return (
      <Container>
        <Row>
          <Col md={12}>
            <h3 className="page-title">Mover alumnos de examen</h3>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <MoveExamInscriptionsForm
              onSubmit={this.handleSubmit}
              onBack={this.handleOnBack}
              onChangeSelect={this.handleChangeSelect}
              onStudentEnrollmentChanges={this.onStudentEnrollmentChanges}
              onCheckAllChanges={this.onCheckAllChanges}
              handleFilterChangeText={this.debouncedHandleFilterChangeText}
              initialValues={{}}
              students={this.getStudents()}
              exams={exams}
              exam={exam}
              filterValue={filterValue}
              checkAll={checkAll}
              sourceExamLabel={`${sourceExam.materia} | ${sourceExam.periodo}`}
              enableReinitialize
            />
          </Col>
        </Row>
      </Container>
    );
  }
}

MoveExamInscriptionsIndex.propTypes = {
  id: PropTypes.string.isRequired,
  history: PropTypes.shape({}).isRequired,
};

export default MoveExamInscriptionsIndex;
